/**
 * SAppBar — SPA-converted version of the legacy server-component AppBar.
 * Pulls user/events/notifications via TanStack Query (was server-side fetch);
 * everything below the suspense fallback renders identically to the legacy.
 */
import { useTranslations } from "next-intl";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { initialiseUserBase } from "@/lib/api/handlers/UserHandling";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";
import { AppBarContent } from "./components/AppBarContent";
import { AppBarStripped } from "./components/AppBarStripped";
import { InitialiseUserBaseResponse } from "@/types/responses/UserResponses";
import { Event } from "@/types/Event";

type SAppBarProps = {
  /**
   * The selected event, already fetched by the layout shell. Threaded in so
   * the appbar doesn't issue a second getEvent. Omitted on the dashboard /
   * error-gate where no event is selected.
   */
  event?: Event | null;
};

/** How often the app-bar seed (bell rows + unread count) is re-read while the tab is in the foreground. */
export const SAPPBAR_REFETCH_INTERVAL_MS = 60_000;

/** One read of the seed, with the moment its bell rows were cut at (`olderThan`). */
type AppBarSeed = InitialiseUserBaseResponse & { fetchDate: Date };

export const SAppBar: React.FC<SAppBarProps> = ({ event = null }) => {
  const t = useTranslations("errors");
  // Only for a cached seed without its own date (none is written so today).
  const [mountDate] = useState(() => new Date());

  // Event-INDEPENDENT query: keyed without eventId so it stays cached across
  // event switches (no refetch storm). keepPreviousData keeps the appbar
  // painted on the very first transitions instead of collapsing to an empty bar.
  const { data, isLoading, error } = useQuery({
    queryKey: ["initialiseUserBase"],
    // Audit r1 A5-3: every read asks for the rows older than ITS OWN moment.
    // A date frozen at mount (the backend filters `createdAt < olderThan`)
    // kept every row created during the visit out of the poll for good. The
    // date travels with the rows: the bell pages on from the same cut.
    //
    // Audit r2 B3-4: a failure is THROWN, not returned. A returned ServerError
    // VALUE replaced the cached good seed, so one failed poll (a 502 during a
    // deploy, a 429 on one of the five reads) swapped the whole bar for the
    // error strip for a minute and unmounted the bell. Thrown, react-query
    // keeps the last good `data` and only sets `error`.
    queryFn: async (): Promise<AppBarSeed> => {
      const fetchDate = new Date();
      const result = await initialiseUserBase({ fetchDate });
      if (isServerErr(result)) {
        throw Object.assign(new Error(result.message), { httpStatus: result.httpStatus });
      }
      return { ...result, fetchDate };
    },
    // As before the throw: no retry burst of five requests each — the next
    // poll (or ConnectionWatcher's recovery refetch) asks again.
    retry: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // W4-d: the bell learns about a new request while the tab is open — a
    // minute's poll, never in a background tab (the organiser is not looking,
    // and a hundred idle tabs must not become a hundred polls).
    refetchInterval: SAPPBAR_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  if (isLoading) {
    return <div className="sticky top-0 z-40 w-full surface-primary h-12" />;
  }

  // The last good seed, kept through a failed poll (B3-4). A ServerError
  // VALUE can still sit in the cache when it was written from outside.
  const response = data && !isServerErr(data) ? (data as AppBarSeed) : null;

  // The strip only for a bar that never had a seed.
  if (response === null) {
    const raw =
      error instanceof Error
        ? error.message
        : ((data as { message?: string } | undefined)?.message ?? "failedInitialisation");
    const message = t.has(raw) ? t(raw) : t("failedInitialisation");
    return <AppBarStripped error={message} />;
  }

  return (
    <div className="sticky top-0 z-40 w-full flex flex-col transition-all duration-300 surface-primary">
      <AppBarContent
        events={response.events!}
        user={response.user!}
        notifications={response.notifications!}
        notificationsUnread={response.notificationsUnread!}
        numUnread={response.numUnread!}
        fetchDate={response.fetchDate ?? mountDate}
        event={event}
      />
    </div>
  );
};
